import { useQueryClient } from "@tanstack/react-query";
import { Card, Logo, useDocumentTitle } from "@unizpay/ui";
import { ShieldCheck } from "lucide-react";
import { WALLET_KEY, useSignOut } from "../lib/queries";
import type { User } from "../lib/types";
import { PinFlow } from "./account/PinPage";

/**
 * The first thing after sign-up (and for any account without one): choose the 6-digit PIN.
 * Together with a code we email, it approves every sensitive action: sending from the wallet,
 * new payout addresses, live API keys and changing the PIN itself. Nothing else opens until it
 * is set.
 */
export function SetPinPage({ user }: { user: User }) {
  useDocumentTitle("Set your PIN");
  const queryClient = useQueryClient();
  const signOut = useSignOut();

  return (
    <div className="min-h-dvh bg-bg">
      <header className="flex items-center justify-between px-4 py-4 sm:px-8">
        <Logo />
        <span className="flex items-center gap-3 text-[0.8125rem] text-fg-muted">
          <span className="hidden sm:inline">{user.email}</span>
          <button
            type="button"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
            className="font-medium text-accent hover:underline"
          >
            Sign out
          </button>
        </span>
      </header>
      <main className="mx-auto max-w-xl px-4 py-8 sm:py-14">
        <div className="mb-6 text-center">
          <span className="mx-auto flex size-11 items-center justify-center rounded-full bg-accent/10">
            <ShieldCheck aria-hidden="true" className="size-5 text-accent" />
          </span>
          <h1 className="mt-4 text-[1.375rem] font-semibold tracking-tight">Secure your account</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Choose a 6-digit PIN. With a code we email you, it approves sending money, new payout addresses
            and live API keys.
          </p>
        </div>
        <Card>
          <PinFlow
            changing={false}
            // The wallet reloads with its PIN set, and the dashboard opens.
            onDone={() => queryClient.invalidateQueries({ queryKey: WALLET_KEY })}
          />
        </Card>
      </main>
    </div>
  );
}
